import cx from "classnames";
import { isEqual } from "lodash";
import { FC, JSX, useContext, useEffect, useState } from "react";
import { BenchmarkingDashboardSnpPlanFilterEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardSnpPlanFilterEnum";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingPlanTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingPlanTypeEnum";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import { buildStateCountyData } from "@app/overviews/network-benchmarking-dashboard/filters/shared/buildStateCountyData";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import { MedicareStarRatingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { MedicareFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareFiltersContext";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import RoundCheckbox from "@app/shared/RoundCheckbox";

const STAR_RATING_VALUES = Object.values(MedicareStarRatingTypeEnum)
  .filter(x => x !== MedicareStarRatingTypeEnum.NA)
  .map(x => parseFloat(x))
  .sort((a, b) => a - b);

interface MedicareAdvancedFiltersProps {
  onDirtyChange: (isDirty: boolean) => void;
}

const MedicareAdvancedFilters: FC<MedicareAdvancedFiltersProps> = (props: MedicareAdvancedFiltersProps): JSX.Element => {
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMedicareFiltersContext = useContext(MedicareFiltersContext);
  const flyoutContext = useContext(FlyoutContext);

  const hasFilterData = !!benchmarkingFiltersContext.states.length;

  // every field below is staged locally and only written back to the shared contexts when Apply is
  // clicked — this keeps edits made in the flyout from leaking into the primary filters bar until then.
  // Line of Business is the exception: it writes straight through to benchmarkingFiltersContext so the
  // primary filters bar's dropdown stays in sync and the primary-filters API call fires immediately.
  const [stagedState, setStagedState] = useState<BenchmarkingState>(benchmarkingFiltersContext.selectedState);
  const [stagedRawCounties, setStagedRawCounties] = useState<BenchmarkingCounty[]>(benchmarkingFiltersContext.rawCounties);
  const [stagedNestedCounties, setStagedNestedCounties] = useState<NestingParentDropdownItem[]>(benchmarkingFiltersContext.counties);
  const [stagedSelectedCounties, setStagedSelectedCounties] = useState<string[]>(benchmarkingFiltersContext.selectedCounties);
  const [stagedStarRatings, setStagedStarRatings] = useState<DropdownItem<MedicareStarRatingTypeEnum>[]>(benchmarkingMedicareFiltersContext.selectedStarRatings);
  const [stagedPlanTypes, setStagedPlanTypes] = useState<DropdownItem<BenchmarkingPlanTypeEnum>[]>(benchmarkingMedicareFiltersContext.selectedPlanTypes);
  const [stagedSnpFilter, setStagedSnpFilter] = useState<BenchmarkingDashboardSnpPlanFilterEnum>(benchmarkingMedicareFiltersContext.selectedSnpFilter);
  const [stagedMembershipCount, setStagedMembershipCount] = useState<MembershipCountTypeEnum>(benchmarkingMedicareFiltersContext.selectedMembershipCount);

  const onApplyClick = (): void => {
    // setSelectedState re-derives counties for the new state on its own, so when the state actually
    // changed, apply the state and the staged county selection atomically — otherwise the
    // derive-counties effect would stomp the staged selection with "all counties in the new state"
    if (stagedState?.stateAbbreviation !== benchmarkingFiltersContext.selectedState?.stateAbbreviation) {
      benchmarkingFiltersContext.setSelectedStateAndCounties(stagedState, stagedSelectedCounties);
    } else {
      benchmarkingFiltersContext.setSelectedCounties(stagedSelectedCounties);
    }

    benchmarkingMedicareFiltersContext.setSelectedStarRatings(stagedStarRatings);
    benchmarkingMedicareFiltersContext.setSelectedPlanTypes(stagedPlanTypes);
    benchmarkingMedicareFiltersContext.setSelectedSnpFilter(stagedSnpFilter);
    benchmarkingMedicareFiltersContext.setSelectedMembershipCount(stagedMembershipCount);

    // applying commits the staged values, so the "unsaved changes" warning shouldn't fire for this close
    flyoutContext.clearWarnOnClose();
    flyoutContext.handleCloseFlyout();
  }

  const isDirty = (): boolean => {
    const countiesEqual = isEqual(
      [...stagedSelectedCounties].sort(),
      [...benchmarkingFiltersContext.selectedCounties].sort()
    );

    const starRatingsEqual = isEqual(
      stagedStarRatings.map(x => x.key).sort(),
      benchmarkingMedicareFiltersContext.selectedStarRatings.map(x => x.key).sort()
    );

    const planTypesEqual = isEqual(
      stagedPlanTypes.map(x => x.key).sort(),
      benchmarkingMedicareFiltersContext.selectedPlanTypes.map(x => x.key).sort()
    );

    return stagedState?.stateAbbreviation !== benchmarkingFiltersContext.selectedState?.stateAbbreviation ||
      !countiesEqual ||
      !starRatingsEqual ||
      !planTypesEqual ||
      stagedSnpFilter !== benchmarkingMedicareFiltersContext.selectedSnpFilter ||
      stagedMembershipCount !== benchmarkingMedicareFiltersContext.selectedMembershipCount;
  }

  useEffect(() => {
    props.onDirtyChange(isDirty());
  });

  const onStagedStateChange = (stateAbbreviation: string): void => {
    const state: BenchmarkingState = benchmarkingFiltersContext.states.find(x => x.stateAbbreviation === stateAbbreviation);

    if (!!state) {
      setStagedState(state);

      const countyData = buildStateCountyData(state, benchmarkingFiltersContext.countyIdFilter);
      setStagedRawCounties(countyData.rawCounties);
      setStagedNestedCounties(countyData.counties);
      setStagedSelectedCounties(countyData.selectedCounties);
    }
  }

  const onCBSAMouseOver = (parentKey?: string): void => {
    if (!!parentKey) {
      const cbsa: NestingParentDropdownItem = stagedNestedCounties.find(x => x.key === parentKey);
      benchmarkingFiltersContext.setMousedOverCountyIds(cbsa?.children.map(x => x.key));
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onCountyCheckboxMouseOver = (dropdownItemKey?: string): void => {
    if (!!dropdownItemKey) {
      benchmarkingFiltersContext.setMousedOverCountyIds([dropdownItemKey]);
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const starRatingKeyFor = (value: number | null): MedicareStarRatingTypeEnum =>
    (value === null ? MedicareStarRatingTypeEnum.NA : value.toString()) as MedicareStarRatingTypeEnum;

  const isStarRatingChecked = (value: number | null): boolean =>
    stagedStarRatings.some(x => x.key === starRatingKeyFor(value));

  const onStarRatingChange = (value: number | null): void => {
    const key = starRatingKeyFor(value);

    if (isStarRatingChecked(value)) {
      setStagedStarRatings(stagedStarRatings.filter(x => x.key !== key));
      return;
    }

    // the star rating lookup may still be loading — ignore the click rather than staging an undefined entry
    const starRating = benchmarkingMedicareFiltersContext.mappedStarRatings.find(x => x.key === key);

    if (!!starRating) {
      setStagedStarRatings([...stagedStarRatings, starRating]);
    }
  }

  return (
    <div className={cx("qes-advanced-filters")}>
      <label className="copy-text-default d-block mb-3">
        Add filter criteria to refine your search results.
      </label>

      <QESSelect
        containerClassName="mb-3"
        disabled={benchmarkingFiltersContext.linesOfBusinessLoading || !benchmarkingFiltersContext.linesOfBusiness.length}
        testId="network-benchmarking-dashboard__advanced-filters--line-of-business"
        label="Line of Business"
        onChange={(e) => benchmarkingFiltersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
        value={benchmarkingFiltersContext.selectedLineOfBusiness || ""}
      >
        {Object.values(benchmarkingFiltersContext.linesOfBusiness).map((lineOfBusiness, index) => (
          <option
            key={index}
            value={lineOfBusiness}
          >
            {BenchmarkingLineOfBusinessEnum[lineOfBusiness]}
          </option>
        ))}
      </QESSelect>

      <QESSelect
        containerClassName="mb-3"
        disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData}
        testId="network-benchmarking-dashboard__advanced-filters--state"
        label="State"
        onChange={(e) => onStagedStateChange(e.target.value)}
        value={stagedState?.stateAbbreviation || ""}
      >
        {benchmarkingFiltersContext.states.map((state, index) => (
          <option
            key={index}
            value={state.stateAbbreviation}
          >
            {state.stateName}
          </option>
        ))}
      </QESSelect>

      <MultiselectNestedDropdown
        containerClassName="mb-3 d-block"
        customSelectInputText={(!!stagedRawCounties.length && stagedRawCounties.length === stagedSelectedCounties.length) ? "All" : null}
        disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData || !stagedState}
        hasDuplicates={true}
        items={stagedNestedCounties}
        label="Service Area"
        onDropdownItemMouseOver={onCountyCheckboxMouseOver}
        onParentMouseOver={onCBSAMouseOver}
        onSelectionChanged={(selectedCounties: string[]) => { setStagedSelectedCounties(selectedCounties) }}
        selectedItems={stagedSelectedCounties}
        testId="network-benchmarking-dashboard__advanced-filters--county"
      />

      <hr />

      <RoundCheckbox
        includesUnknown={true}
        isChecked={isStarRatingChecked}
        label="CMS Star Rating"
        onChange={onStarRatingChange}
        testId="network-benchmarking-dashboard__advanced-filters--contract-star-rating"
        unknownLabel="N/A"
        values={STAR_RATING_VALUES}
      />

      <MultiselectDropdown
        containerClassName="mb-3 mt-3 d-block"
        customSelectInputText={(benchmarkingMedicareFiltersContext.mappedPlanTypes.length === stagedPlanTypes.length) ? "All" : null}
        disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData}
        items={benchmarkingMedicareFiltersContext.mappedPlanTypes}
        onSelectionChanged={(selectedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[]) => { setStagedPlanTypes(selectedPlanTypes) }}
        label="Plan Type"
        selectedItems={stagedPlanTypes}
        testId="network-benchmarking-dashboard__advanced-filters--contract-plan-types"
      />

      <QESSelect
        containerClassName="mb-3"
        disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData}
        testId="network-benchmarking-dashboard__advanced-filters--snp-plan"
        label="SNP Plan"
        onChange={(e) => setStagedSnpFilter(e.target.value as BenchmarkingDashboardSnpPlanFilterEnum)}
        value={stagedSnpFilter || ""}
      >
        {Object.values(BenchmarkingDashboardSnpPlanFilterEnum).map((snpPlanFilter, index) => (
          <option
            key={index}
            value={snpPlanFilter}
          >
            {`${snpPlanFilter}`}
          </option>
        ))}
      </QESSelect>

      <QESSelect
        containerClassName="mb-3"
        disabled={benchmarkingMedicareFiltersContext.primaryFiltersLoading || !hasFilterData}
        testId="network-benchmarking-dashboard__advanced-filters--membership-count"
        label="Membership Count"
        onChange={(e) => setStagedMembershipCount(e.target.value as MembershipCountTypeEnum)}
        value={stagedMembershipCount || ""}
      >
        {Object.values(MembershipCountTypeEnum).map((membershipCount, index) => (
          <option
            key={index}
            value={membershipCount}
          >
            {`${membershipCount}${membershipCount !== MembershipCountTypeEnum.All ? "+" : ""}`}
          </option>
        ))}
      </QESSelect>

      <FlyoutFooter
        submitButton={
          <QESButton
            disabled={false}
            qesTestId="network-benchmarking-dashboard__advanced-filters--apply"
            qesType="primary"
            onClick={onApplyClick}
          >
            Apply
          </QESButton>
        }
      />
    </div>
  );
}

export default MedicareAdvancedFilters;
